<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import V2ReservationsDayGroup from './V2ReservationsDayGroup.vue';
  import { groupDayReservations } from '@/domain/reservations/reservation-day-grouping';
  import type {
    IReservation,
    IReservationDayShift,
  } from '@/interfaces/IReservation';
  const props = defineProps<{
    reservations: IReservation[];
    shifts: IReservationDayShift[];
    now: Date;
    timeZone: string;
    toleranceMin: number;
    businessDayStartHour?: number;
    selectedId: number | null;
    filtered: boolean;
    /** Shifts exist but none opens on this date. */
    closedDay?: boolean;
  }>();
  const emit = defineEmits<{
    (event: 'select', id: number): void;
    (event: 'create'): void;
  }>();
  const groups = computed(() =>
    groupDayReservations(
      props.reservations,
      props.shifts,
      props.timeZone
    ).filter((group) => group.reservations.length)
  );
  const emptyKeys = computed(() =>
    props.filtered
      ? {
          title: 'reservations.list.emptyFilterTitle',
          description: 'reservations.list.emptyFilterDescription',
        }
      : props.closedDay
      ? {
          title: 'reservations.list.closedTitle',
          description: 'reservations.list.closedDescription',
        }
      : {
          title: 'reservations.list.emptyTitle',
          description: 'reservations.list.emptyDescription',
        }
  );
</script>

<template>
  <div class="reservations-day-list">
    <PosUiEmptyState
      v-if="!reservations.length"
      icon="calendar-blank-outline"
      :title="$t(emptyKeys.title)"
      :description="$t(emptyKeys.description)"
    >
      <template v-if="!filtered" #action
        ><PosUiButton
          icon="plus"
          color="neutral"
          variant="outline"
          @click="emit('create')"
          >{{ $t('reservations.bar.new') }}</PosUiButton
        ></template
      >
    </PosUiEmptyState>
    <V2ReservationsDayGroup
      v-for="(group, index) in groups"
      :key="group.shift ? `${group.shift.id}:${index}` : 'outside'"
      :group="group"
      :now="now"
      :time-zone="timeZone"
      :tolerance-min="toleranceMin"
      :business-day-start-hour="businessDayStartHour"
      :selected-id="selectedId"
      @select="emit('select', $event)"
    />
  </div>
</template>

<style scoped lang="scss">
  .reservations-day-list {
    @apply tw-p-3 md:tw-p-4;
  }
</style>
